import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const primary = "bg-foreground text-background hover:bg-accent hover:text-accent-foreground";
const secondary = "border border-foreground bg-background text-foreground hover:bg-secondary";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill text-[15px] font-medium leading-none transition-colors duration-150 ease-[cubic-bezier(.2,0,0,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ring-offset-background disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: primary,
        hero: primary,
        destructive: "bg-destructive text-destructive-foreground hover:opacity-90",
        outline: secondary,
        secondary,
        "hero-secondary": secondary,
        neo: secondary,
        onDark: "bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        onDarkSecondary: "border border-background bg-transparent text-background hover:bg-background/10",
        ghost: "hover:bg-secondary",
        link: "rounded-none px-0 text-foreground underline-offset-4 hover:underline",
      },
      size: {
        default: "h-12 px-6",
        sm: "h-9 px-4 text-sm",
        lg: "h-12 px-6",
        xl: "h-12 px-6",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
